import classNames from "classnames";
import classes from "../SegmentCommonStyles.module.less";
import { Card } from "~/Configurators/Linear/components/Card";
import { SimpleCsticNumberField } from "~/Configurators/Shaft/components/SimpleCsticNumberField";
import { SimpleCsticSelectField } from "~/Configurators/Shaft/components/SimpleCsticSelectField";
import { SimpleCsticId } from "~/Configurators/Linear/models";
import { selectCstic, selectCsticExpanded } from "~/Configurators/Linear/redux/selectCstic";
import { useSelector } from "react-redux";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { chamfer } from "~/Configurators/Shaft/inch/inchCsticConstants";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { SimpleCsticTextField } from "~/Configurators/Shaft/components/SimpleCsticTextField";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import {
  isChamferManualWidth,
  validateChamferSegment,
  type ValidationMode,
} from "~/Configurators/Shaft/shaftConfiguratorHelper";
import type { RootState } from "~/redux/store";
import {
  ErrorMessage,
  selectIsMachiningSubstepInvalid,
} from "~/Configurators/Shaft/metric/components/MetricShaftMachiningExtraSubstep/MetricShaftMachiningExtraSubstepHelper";
import { selectMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { selectConflictedCstics } from "~/Configurators/Linear/redux/selectors";

import { KmatType } from "~/Configurators/Linear/kmatType";

interface Props {
  className?: string;
  side: "left" | "right";
}

const CONFIG = {
  left: {
    chamferAngle: {
      csticId: SimpleCsticId(chamfer.left.angle),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.title",
      csticsToNull: [chamfer.left.width.sf],
    },
    chamferWidthSF: {
      csticId: SimpleCsticId(chamfer.left.width.sf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    chamferWidthNF: {
      csticId: SimpleCsticId(chamfer.left.width.nf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    minmax: SimpleCsticId(chamfer.left.width.minmax),
    image: "configurator.shaft.step.2.substep.3.extraFields.left.image",
  },
  right: {
    chamferAngle: {
      csticId: SimpleCsticId(chamfer.right.angle),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.title",
      csticsToNull: [chamfer.right.width.sf],
    },
    chamferWidthSF: {
      csticId: SimpleCsticId(chamfer.right.width.sf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    chamferWidthNF: {
      csticId: SimpleCsticId(chamfer.right.width.nf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    minmax: SimpleCsticId(chamfer.right.width.minmax),
    image: "configurator.shaft.step.2.substep.3.extraFields.right.image",
  },
};

export const InchShaftChamferSegment = (props: Props) => {
  const t = useXmlConfiguratorTranslations().t!;

  function translate<T extends { label: string }>(data: T) {
    return { ...data, label: t(data.label) };
  }

  const config = CONFIG[props.side];
  const angleCstic = useSelector(selectCstic(config.chamferAngle.csticId));
  const minmaxCstic = useSelector(selectCstic(config.minmax));

  const helperLabel = minmaxCstic?.value;

  const isWidthEditable = isChamferManualWidth(angleCstic?.value);

  const errors = useSelector(
    selectErrors({
      angle: config.chamferAngle.csticId.key,
      width: {
        sf: config.chamferWidthSF.csticId.key,
        nf: config.chamferWidthNF.csticId.key,
      },
    }),
  );
  const invalidWidth = !!errors.find((error) => error.cstic === config.chamferWidthSF.csticId.key);
  const invalidAngle = !invalidWidth && !!errors.find((error) => error.cstic === config.chamferAngle.csticId.key);

  return (
    <div className={classNames(classes.root, props.className)}>
      <Card className={classes.imageSection}>
        <img className={classes.image} src={t(config.image)} alt="" />
      </Card>
      <div className={classes.inputSection}>
        {invalidAngle && <ErrorMessage />}
        <SimpleCsticSelectField
          {...translate(config.chamferAngle)}
          substepType={SubstepType.ShaftMachining}
          shrink={true}
          renderValue={(_, index) => {
            return (
              // <Stacker direction={"row"} gap={8}>
              <LanguageAwareExternalContent content={DROPDOWN_LABELS[index]} />
              // <LinearTileV2.StandardIndicator />
              // </Stacker>
            );
          }}
        />
        {isWidthEditable && (
          <>
            {invalidWidth && <ErrorMessage variant={"input"} />}
            <SimpleCsticNumberField
              {...translate(config.chamferWidthSF)}
              substepType={SubstepType.ShaftMachining}
              decimalScale={4}
              helperLabel={helperLabel}
              required
            />
          </>
        )}
        {!isWidthEditable && (
          <SimpleCsticTextField
            {...translate(config.chamferWidthNF)}
            substepType={SubstepType.ShaftMachining}
            disabled={true}
          />
        )}
      </div>
    </div>
  );
};

const DROPDOWN_LABELS = [
  "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.option1",
  "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.option2",
];

// Get the 2nd param of isAxialThreadFullyConfigured as a type
type Config = Parameters<typeof validateChamferSegment>[0];
const selectErrors = (config: Config) => (state: RootState) => {
  const isSubstepInvalid = selectIsMachiningSubstepInvalid(state);
  const { isDirty } = selectCsticExpanded(
    SimpleCsticId(config.width.sf, KmatType.Main),
    SubstepType.ShaftMachining,
  )(state);

  const mode: ValidationMode = isDirty || isSubstepInvalid ? "full" : "feedback";
  const kmat = selectMainKmat(state);
  const conflictedCstics = selectConflictedCstics(state);
  return validateChamferSegment(config, { kmat, conflictedCstics, mode });
};
